import React, { useState, useCallback, useRef } from 'react';
import { Input, Select, Switch, Button, message } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { inviteProviders, checkProviderEmail } from 'api/group';
import GenderSelect from 'components/form/GenderSelect';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields, SettingEnum } from 'types/enumerationData';
import { TInviteProviderItem } from 'types/group';
import s from './s.module.less';

type Props = {
    onBack: () => void;
    onClose: () => void;
};

const EMPTY_PROVIDER: TInviteProviderItem = {
    firstName: '',
    lastName: '',
    credential: '',
    gender: '',
    email: '',
    isAdmin: false,
};

const InviteProvidersStep: React.FC<Props> = ({ onBack, onClose }) => {
    const [providers, setProviders] = useState<TInviteProviderItem[]>([{ ...EMPTY_PROVIDER }]);
    const [credentialSelections, setCredentialSelections] = useState<Record<number, string[]>>({ 0: [] });
    const [loading, setLoading] = useState(false);
    // Per-row email errors (keyed by provider index), populated by the email check.
    const [emailErrors, setEmailErrors] = useState<Record<number, string>>({});
    // Per-row in-flight email checks (keyed by provider index) — blocks sending until they resolve.
    const [checkingEmails, setCheckingEmails] = useState<Record<number, boolean>>({});
    const [credentialTypes] = useGetSettingData(EnumFields.CREDENTIAL_TYPE);
    // Cache of backend check results keyed by trimmed email, so blur and pre-send share one request per email.
    const emailCheckCacheRef = useRef<Record<string, { addable: boolean; message?: string }>>({});

    // Resolve a single email's check result, reusing the cache to avoid re-querying the same address.
    const checkEmail = useCallback(async (trimmed: string) => {
        if (trimmed in emailCheckCacheRef.current) {
            return emailCheckCacheRef.current[trimmed];
        }
        const { data } = await checkProviderEmail(trimmed);
        if (data?.code !== 200 || !data?.data) return null;
        const result = { addable: !!data.data.addable, message: data.data.message };
        emailCheckCacheRef.current[trimmed] = result;
        return result;
    }, []);

    const handleFieldChange = useCallback(
        (index: number, field: keyof TInviteProviderItem, value: string | boolean) => {
            setProviders((prev) => {
                const updated = [...prev];
                updated[index] = { ...updated[index], [field]: value };
                return updated;
            });
            // Editing the email clears its previous server-side error.
            if (field === 'email') {
                setEmailErrors((prev) => {
                    if (!(index in prev)) return prev;
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
            }
        },
        [],
    );

    // Check the email against the backend on blur; surface the message when it's not addable.
    const handleEmailBlur = useCallback(
        async (index: number, email: string) => {
            const trimmed = email.trim();
            if (!trimmed) return;
            setCheckingEmails((prev) => ({ ...prev, [index]: true }));
            try {
                const result = await checkEmail(trimmed);
                if (!result) return;
                setEmailErrors((prev) => {
                    const next = { ...prev };
                    if (result.addable) {
                        delete next[index];
                    } else {
                        next[index] = result.message || 'This email cannot be added';
                    }
                    return next;
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                setCheckingEmails((prev) => {
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
            }
        },
        [checkEmail],
    );

    // Re-check every non-empty email before sending, since a freshly-typed email may never have
    // triggered the blur check. Cached results make already-checked emails free. Returns the error
    // map for non-addable emails.
    const runEmailChecks = useCallback(async (): Promise<Record<number, string>> => {
        const errors: Record<number, string> = {};
        await Promise.all(
            providers.map(async (p, index) => {
                const trimmed = p.email.trim();
                if (!trimmed) return;
                try {
                    const result = await checkEmail(trimmed);
                    if (result && !result.addable) {
                        errors[index] = result.message || 'This email cannot be added';
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                }
            }),
        );
        return errors;
    }, [providers, checkEmail]);

    const handleCredentialChange = useCallback((index: number, values: string[]) => {
        setCredentialSelections((prev) => ({ ...prev, [index]: values }));
        setProviders((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], credential: values.join(', ') };
            return updated;
        });
    }, []);

    const handleAdd = useCallback(() => {
        setProviders((prev) => [...prev, { ...EMPTY_PROVIDER }]);
        setCredentialSelections((prev) => ({ ...prev, [Object.keys(prev).length]: [] }));
    }, []);

    const handleRemove = useCallback((index: number) => {
        setProviders((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const hasEmailError = Object.keys(emailErrors).length > 0;
    const isCheckingEmail = Object.keys(checkingEmails).length > 0;
    const canSend =
        !hasEmailError &&
        !isCheckingEmail &&
        providers.some((p) => p.firstName.trim() && p.lastName.trim() && p.credential && p.gender && p.email.trim());

    const handleSendInvite = useCallback(async () => {
        if (!canSend || loading) return;
        const validProviders = providers.filter(
            (p) => p.firstName.trim() && p.lastName.trim() && p.credential && p.gender && p.email.trim(),
        );
        if (!validProviders.length) return;
        setLoading(true);
        try {
            // Catch emails that were typed but never blurred before clicking send.
            const errors = await runEmailChecks();
            if (Object.keys(errors).length) {
                setEmailErrors(errors);
                return;
            }
            const { data } = await inviteProviders({ providers: validProviders, sendInvite: true });
            if (data.code === 200) {
                const results: { email?: string; errorMessage?: string }[] = data?.data?.results || [];
                const failed = results.filter((r) => r.errorMessage);
                if (failed.length) {
                    message.error({
                        icon: <span />,
                        content: (
                            <div className={s.inviteErrors}>
                                <div className={s.inviteErrorsTitle}>
                                    <CloseCircleFilled className={s.inviteErrorsIcon} />
                                    <span>Some providers couldn&apos;t be invited:</span>
                                </div>
                                <ul className={s.inviteErrorsList}>
                                    {failed.map((r, i) => (
                                        <li key={i}>
                                            <span className={s.inviteErrorEmail}>{r.email || 'Email'}</span>
                                            {` — ${r.errorMessage}`}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ),
                    });
                } else {
                    message.success('Invitations sent');
                }
                onClose();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [canSend, loading, providers, onClose, runEmailChecks]);

    return (
        <>
            <div className={s.body}>
                {providers.map((provider, index) => (
                    <div key={index} className={s.providerBlock}>
                        <div className={s.providerLabel}>
                            <span className={s.providerIndex}>Provider {index + 1}</span>
                            {providers.length > 1 && (
                                <Button size="small" className={s.removeBtn} onClick={() => handleRemove(index)}>
                                    Remove
                                </Button>
                            )}
                        </div>
                        <div className={s.providerCard}>
                            <div className={s.fieldRow}>
                                <div className={s.fieldGroup}>
                                    <label className={s.fieldLabel}>
                                        First name <span className={s.required}>*</span>
                                    </label>
                                    <Input
                                        value={provider.firstName}
                                        onChange={(e) => handleFieldChange(index, 'firstName', e.target.value)}
                                    />
                                </div>
                                <div className={s.fieldGroup}>
                                    <label className={s.fieldLabel}>
                                        Last name <span className={s.required}>*</span>
                                    </label>
                                    <Input
                                        value={provider.lastName}
                                        onChange={(e) => handleFieldChange(index, 'lastName', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className={s.fieldRow}>
                                <div className={s.fieldGroup}>
                                    <label className={s.fieldLabel}>
                                        Credentials <span className={s.required}>*</span>
                                    </label>
                                    <Select
                                        mode="multiple"
                                        showArrow
                                        value={credentialSelections[index] || []}
                                        onChange={(vals: string[]) => handleCredentialChange(index, vals)}
                                        fieldNames={{ label: 'content', value: 'dictKey' }}
                                        options={credentialTypes as SettingEnum[]}
                                        placeholder="Select credentials"
                                    />
                                </div>
                                <div className={s.fieldGroup}>
                                    <label className={s.fieldLabel}>
                                        Gender <span className={s.required}>*</span>
                                    </label>
                                    <GenderSelect
                                        value={provider.gender || undefined}
                                        onChange={(val: string) => handleFieldChange(index, 'gender', val)}
                                        placeholder="Select gender"
                                    />
                                </div>
                            </div>
                            <div className={s.fieldGroup}>
                                <label className={s.fieldLabel}>
                                    Email <span className={s.required}>*</span>
                                </label>
                                <Input
                                    status={emailErrors[index] ? 'error' : undefined}
                                    value={provider.email}
                                    onChange={(e) => handleFieldChange(index, 'email', e.target.value)}
                                    onBlur={(e) => handleEmailBlur(index, e.target.value)}
                                />
                                {emailErrors[index] && <div className={s.fieldError}>{emailErrors[index]}</div>}
                            </div>
                            <div className={s.adminRow}>
                                <Switch
                                    checked={provider.isAdmin}
                                    onChange={(checked) => handleFieldChange(index, 'isAdmin', checked)}
                                />
                                <div className={s.adminText}>
                                    <div className={s.adminTitle}>Practice-wide admin access</div>
                                    <div className={s.adminDesc}>
                                        Grants access to system configuration, user management, billing oversight, and
                                        practice-level settings. Overrides role-based permissions.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <div className={s.addAnother} onClick={handleAdd}>
                    Add another provider
                </div>
            </div>
            <div className={s.footer}>
                <div className={s.footerBorder} />
                <div className={s.footerActions}>
                    <Button className={s.backBtn} onClick={onBack}>
                        Back
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSend}
                        loading={loading}
                        className={canSend ? s.primaryBtn : s.disabledBtn}
                        onClick={handleSendInvite}
                    >
                        Send invite
                    </Button>
                </div>
            </div>
        </>
    );
};

export default InviteProvidersStep;
